// useAiEndpointConfig — user-konfigurerbar OpenAI-kompatibel endpoint
// för AI-assistenten (extraherad ur AiAssistantDialog, #1 i
// PRE_RELEASE_REVIEW_100). Äger load/persist (localStorage +
// sessionStorage-separerad apiKey), draft-state, test-connection med
// allowlist-gate, och chat-URL-resolutionen.

import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from '@/hooks/use-toast';
import { IS_DESKTOP } from '@/lib/buildMode';
import { normalizeChatEndpoint } from '@/lib/normalizeChatEndpoint';
import { isAllowedAiEndpoint, explainAiRejection } from '@/lib/aiEndpointAllowlist';

const STORAGE_KEY = 'engicoro-ai-config';

export interface AiConfig {
  endpoint: string;
  apiKey: string;
  model: string;
}

// Security: apiKey separeras till sessionStorage (rensas när fliken stängs).
// localStorage är läsbar av alla browser-extensions med `storage`-perm och
// persisterar för evigt — onödigt att hålla en bearer-key där. endpoint +
// model lagras fortsatt i localStorage (icke-känsligt).
const API_KEY_STORAGE_KEY = `${STORAGE_KEY}-apikey`;

export function loadConfig(): AiConfig {
  let base: AiConfig = { endpoint: '', apiKey: '', model: '' };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<AiConfig>;
      // Migrera legacy apiKey ur localStorage till sessionStorage.
      if (parsed.apiKey) {
        try { sessionStorage.setItem(API_KEY_STORAGE_KEY, parsed.apiKey); } catch { /* ignore */ }
        delete parsed.apiKey;
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed)); } catch { /* ignore */ }
      }
      base = { endpoint: parsed.endpoint ?? '', apiKey: '', model: parsed.model ?? '' };
    }
  } catch { /* ignore */ }
  try {
    const apiKey = sessionStorage.getItem(API_KEY_STORAGE_KEY);
    if (apiKey) base.apiKey = apiKey;
  } catch { /* ignore */ }
  return base;
}

export function saveConfig(config: AiConfig) {
  const { apiKey, ...rest } = config;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(rest));
  try {
    if (apiKey) sessionStorage.setItem(API_KEY_STORAGE_KEY, apiKey);
    else sessionStorage.removeItem(API_KEY_STORAGE_KEY);
  } catch { /* ignore */ }
}

const DEFAULT_SUPABASE_URL = IS_DESKTOP
  ? '' // Desktop: no Supabase fallback — user must configure their own endpoint
  : `${import.meta.env.VITE_SUPABASE_URL || ''}/functions/v1/ai-workflow-assistant`;

export type TestStatus = 'idle' | 'testing' | 'success' | 'error';

export function useAiEndpointConfig({ onSaved }: { onSaved: () => void }) {
  const [config, setConfig] = useState<AiConfig>(loadConfig);
  const [configDraft, setConfigDraft] = useState<AiConfig>(config);
  const [testStatus, setTestStatus] = useState<TestStatus>('idle');
  const testAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => { testAbortRef.current?.abort(); };
  }, []);

  // Desktop: always require user-configured endpoint. Web: Supabase fallback exists
  const isConfigured = IS_DESKTOP
    ? !!(config.endpoint && config.apiKey)
    : !!(config.endpoint && config.apiKey) || !!DEFAULT_SUPABASE_URL;

  // Build the actual chat URL based on config.
  // S-028: använder shared `normalizeChatEndpoint` så user-AI och admin-AI
  // har identisk parsing (trailing-slash-strip + suffix-applikation).
  const getChatUrl = useCallback(() => {
    return normalizeChatEndpoint(config.endpoint) ?? DEFAULT_SUPABASE_URL;
  }, [config.endpoint]);

  /** Persist + aktivera en ny config direkt (t.ex. model-fallback E-009). */
  const persistConfig = useCallback((next: AiConfig) => {
    saveConfig(next);
    setConfig(next);
  }, []);

  const handleSaveConfig = useCallback(() => {
    const cleaned: AiConfig = {
      endpoint: configDraft.endpoint.trim(),
      apiKey: configDraft.apiKey.trim(),
      model: configDraft.model.trim(),
    };
    setConfig(cleaned);
    saveConfig(cleaned);
    onSaved();
    toast({ title: 'Configuration Saved', description: 'AI endpoint settings have been saved.' });
  }, [configDraft, onSaved]);

  const handleTestConnection = useCallback(async () => {
    setTestStatus('testing');
    const base = configDraft.endpoint.trim().replace(/\/+$/, '');
    const url = (base.includes('/v1/chat/completions') || base.includes('/api/chat'))
      ? base
      : `${base}/v1/chat/completions`;

    // S-024: test-connection POSTar apiKey som Bearer + dummy-payload mot
    // user-angiven URL. Samma allowlist-gate som sendMessages — annars
    // läcker API-keyen (och ping-request:en) till godtycklig host.
    if (!isAllowedAiEndpoint(url, {
      supabaseHost: import.meta.env.VITE_SUPABASE_URL,
      allowLocalhost: import.meta.env.DEV,
    })) {
      const reason = explainAiRejection(url);
      setTestStatus('error');
      toast({ title: 'Endpoint blockerad', description: reason, variant: 'destructive' });
      setTimeout(() => setTestStatus('idle'), 3000);
      return;
    }

    testAbortRef.current?.abort();
    const controller = new AbortController();
    testAbortRef.current = controller;

    try {
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${configDraft.apiKey.trim()}`,
        },
        body: JSON.stringify({
          messages: [{ role: 'user', content: 'ping' }],
          stream: false,
          max_tokens: 5,
          ...(configDraft.model.trim() ? { model: configDraft.model.trim() } : {}),
        }),
        signal: controller.signal,
      });
      if (resp.ok) {
        setTestStatus('success');
        setTimeout(() => setTestStatus('idle'), 3000);
      } else {
        setTestStatus('error');
        const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }));
        toast({ title: 'Connection Failed', description: err.error?.message || err.error || `HTTP ${resp.status}`, variant: 'destructive' });
        setTimeout(() => setTestStatus('idle'), 3000);
      }
    } catch (e: unknown) {
      if ((e as { name?: string })?.name === 'AbortError') {
        setTestStatus('idle');
        return;
      }
      setTestStatus('error');
      toast({ title: 'Connection Failed', description: e instanceof Error ? e.message : 'Could not reach the endpoint', variant: 'destructive' });
      setTimeout(() => setTestStatus('idle'), 3000);
    }
  }, [configDraft]);

  return {
    config,
    configDraft,
    setConfigDraft,
    testStatus,
    isConfigured,
    getChatUrl,
    persistConfig,
    handleSaveConfig,
    handleTestConnection,
  };
}
